import styled from '@emotion/styled';
import { AdAnalyticsVideo } from '@kaizenplatform/hugot-entity';
import { List } from 'immutable';
import { createElement, FC, useState } from 'react';

import Language from '../../../entity/Language';
import AspectRatioFixed from '../../component/primitive/AspectRatioFixed';
import ControlableVideoScreen from '../../component/primitive/ControlableVideoScreen';
import { SPACING_LARGE, SPACING_REGULAR, SPACING_UNIT } from '../../constant/spacing';
import DateTable from './DataTable';
import _ImagesPerKeyFrames from './ImagesPerKeyFrames';
import _KeywordKeyframes from './KeywordKeyframes';
// import { Typography } from '@material-ui/core';
import _VideoInfoTable from './VideoInfoTable';
import _WatchingRate from './WatchingRate';

type Props = {
  analyticsVideo: AdAnalyticsVideo;
  language: Language;
  className?: string;
};

const AnalyticsItem: FC<Props> = ({
  analyticsVideo,
  language,
  className,
}: {
  analyticsVideo: AdAnalyticsVideo;
  language: Language;
  className?: string;
}) => {
  const { videoUrl, thumbnailImageUrl, duration } = analyticsVideo;
  const [selectedPlayTiming, updateSelectedPlayTiming] = useState(-1);

  const keywords = analyticsVideo.adAnalyticsVideoIndex ? analyticsVideo.adAnalyticsVideoIndex.keywords : List([]);
  const keyFrames = analyticsVideo.adAnalyticsVideoIndex ? analyticsVideo.adAnalyticsVideoIndex.keyFrames : List([]);
  const watchingRatings = analyticsVideo.adAnalyticsVideoIndex
    ? analyticsVideo.adAnalyticsVideoIndex.watchingRatings
    : List([]);

  const setPlayTiming = (timing: number) => {
    updateSelectedPlayTiming(timing + Math.random() * 0.00001);
  };

  return (
    <Root className={className}>
      {/* {project && <Title variant="h6">{project.title}</Title>} */}
      <CreativeTitle>{`${analyticsVideo.title}`}</CreativeTitle>
      <PlacementListWrapper>
        <PlacementList>
          {analyticsVideo.placements.map((placement) => (
            <PlacementListItem key={placement.key}>
              <PlacementListItemBody>{placement.name}</PlacementListItemBody>
            </PlacementListItem>
          ))}
        </PlacementList>
      </PlacementListWrapper>
      <Analytics>
        <Media>
          <VideoOrImage aspectRatio={[4, 3]}>
            <ControlableVideoScreen
              src={videoUrl ? videoUrl : ''}
              poster={thumbnailImageUrl}
              playPosition={selectedPlayTiming}
              controls
              nodownload
            />
          </VideoOrImage>

          <DateTable analyticsVideo={analyticsVideo} />
        </Media>

        <Info>
          {/* {project ? <VideoInfoTable analyticsVideo={analyticsVideo} project={project} /> : null} */}
          {keyFrames.size > 0 && keywords.size > 0 ? (
            <ImagesPerKeyFrames keyFrames={keyFrames} duration={duration} onUpdatePlayTiming={setPlayTiming} />
          ) : null}
          {keywords.size > 0 || duration > 0 ? (
            <KeywordKeyframes keywords={keywords} duration={duration} language={language} />
          ) : null}
          {watchingRatings.size > 0 ? <WatchingRate watchingRatings={watchingRatings} language={language} /> : null}
        </Info>
      </Analytics>
    </Root>
  );
};

const Root = styled('div')({
  display: 'flex',
  flexDirection: 'column',
});

// const Title = styled(Typography)({
//   marginBottom: SPACING_REGULAR,
//   wordBreak: 'break-word',
// });

const PlacementListWrapper = styled('div')({
  display: 'flex',
  justifyContent: 'space-between',
  marginBottom: '16px',
});

const PlacementList = styled('div')({
  display: 'flex',
  flexWrap: 'wrap',
  width: '80%',
});

const PlacementListItem = styled('div')({
  display: 'flex',
  position: 'relative',
});

const PlacementListItemBody = styled('div')({
  display: 'flex',
  alignItems: 'center',
  fontSize: '14px',
  color: 'white',
  backgroundColor: '#0070C9',
  borderRadius: '8px',
  padding: '4px 8px',
  marginRight: '8px',
  marginBottom: '8px',
  minHeight: '24px',
});

const Analytics = styled('div')({
  display: 'flex',
});

const CreativeTitle = styled('h3')({
  color: 'rgba(0,0,0,.66)',
  marginBottom: '16px',
});

const Media = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  width: '35%',
});

const VideoOrImage = styled(AspectRatioFixed)({
  display: 'flex',
});

const Info = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  marginLeft: SPACING_LARGE,
  width: '65%',
});

const ImagesPerKeyFrames = styled(_ImagesPerKeyFrames)({
  display: 'flex',
  marginBottom: SPACING_REGULAR,
});

const KeywordKeyframes = styled(_KeywordKeyframes)({
  marginBottom: SPACING_UNIT,
});

const WatchingRate = styled(_WatchingRate)({});

export default AnalyticsItem;
